import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormControl, FormGroup} from '@angular/forms';
import {BehaviorSubject, combineLatest, finalize, forkJoin, Observable, tap} from 'rxjs';
import {Document, TimeAppDocumentsService} from '../services/time-app-documents.service';
import {ActivatedRoute} from '@angular/router';
import {filter, map, startWith} from 'rxjs/operators';
import {DokumentModels} from '../../models/dokument-models';
import {v4 as uuidv4} from 'uuid';
import {MessageBoxService} from '../../services/message-box.service';
import {UserAuthenticationService} from '../../services/user-authentication.service';
import DokumentSource = DokumentModels.DokumentSource;
import DokumentTyp = DokumentModels.DokumentTyp;

@Component({
    selector: 'app-time-app-documents-component',
    templateUrl: './time-app-documents.component.html',
    styleUrl: './time-app-documents.component.scss'
})
export class TimeAppDocumentsComponent implements OnInit {

    readonly searchForm = new FormGroup({
        searchInput: new FormControl(''),
    });
    protected filteredDocuments$: Observable<Document[]>;
    private documents$: Observable<Document[]>;
    private readonly searchUpdate = this.searchForm.get('searchInput')!.valueChanges.pipe(
        takeUntilDestroyed(),
        filter(() => this.searchForm.get('searchInput')!.dirty),
        startWith('')
    );
    private sorting$: BehaviorSubject<string> = new BehaviorSubject('document');
    private destroyRef = inject(DestroyRef);
    private currentOrderID$ = new BehaviorSubject('');

    constructor(private timeAppDocumentService: TimeAppDocumentsService, private route: ActivatedRoute,
                private svcMessageBox: MessageBoxService, private userAuth: UserAuthenticationService) {
        this.documents$ = this.timeAppDocumentService.getDocuments();
        this.filteredDocuments$ = combineLatest([this.documents$, this.searchUpdate, this.sorting$]).pipe(
            takeUntilDestroyed(),
            map(value => {
                const searchString = (value[1] || '').toLocaleLowerCase();
                return value[0]
                    .filter(doc => doc.documentName.toLocaleLowerCase().includes(searchString))
                    .sort((a, b) => this.sortDocuments(a, b, value[2]));
            })
        );

    }

    ngOnInit(): void {
        this.route.params.pipe(
            takeUntilDestroyed(this.destroyRef),
            tap(params => {
                this.currentOrderID$.next(params.orderID);
            })
        ).subscribe();

        this.currentOrderID$.pipe(
            tap(id => {
                if (id) {
                    this.timeAppDocumentService.loadDocuments(id);
                } else {
                    this.svcMessageBox.showSuccessByLanguageKey('MAIN_APP_TIME.ORDER_NOT_FOUND.text', '');
                }
            })).subscribe();
    }

    protected async downloadDocument(documentID: any): Promise<void> {
        await this.timeAppDocumentService.downloadDocument(documentID);
    }

    protected async showDocument(documentID: any): Promise<void> {
        await this.timeAppDocumentService.showDocument(documentID);
    }

    protected async uploadDocument(event, uploader: HTMLInputElement): Promise<void> {
        const uploads: Observable<any>[] = [];
        for (let i = 0; i < event.target.files.length; i++) {
            const dialogRef = this.svcMessageBox.showFileNameDialog(event.target.files[i].name);
            const data = (await dialogRef);
            if (data.doSave) {
                const name = this.timeAppDocumentService.getNewFileName(data.value);

                const info: DokumentModels.DokumentInfo = {
                    objectID: this.currentOrderID$.getValue(),
                    documentID: uuidv4(),
                    lastModified: new Date(),
                    fileName: name,
                    description: name.substring(0, name.lastIndexOf('.')),
                    dokumentTyp: ['png', 'jpg', 'jpeg', 'bmp', 'gif'].includes(name.substring(name.lastIndexOf('.'))) ? DokumentTyp.Foto : DokumentTyp.Dokument,
                    dokumentQuelle: DokumentSource.SA_DBBilder,
                };

                uploads.push(this.timeAppDocumentService.uploadDokumentWithInfo(info, event.target.files[i]));
            }
        }
        forkJoin(uploads).pipe(
            finalize(() => {

                uploader.value = '';
                this.timeAppDocumentService.loadDocuments(this.currentOrderID$.getValue());
            }),
        ).subscribe({
            next: value => {
                value.forEach(res => {
                    // imagine sending a code instead of a string message when it works :3
                    this.svcMessageBox.showSuccess(res.value, 'Erfolgreich');
                });
            },
            error: (err) => {
                this.svcMessageBox.showError(err.error.value, 'Fehler');
                console.log(err);
            }
        });
    }

    protected async setSorting(s: string): Promise<void> {
        this.sorting$.next(s);
    }

    protected deleteDocument(documentID: any): void {
        this.timeAppDocumentService.deleteDoku(this.currentOrderID$.getValue(), documentID);
    }

    protected canDelete(documentID: string): boolean {
        return this.timeAppDocumentService.getDocument(documentID)?.modifiedBy === this.userAuth.getCurrentAuthenticatedUser()?.loginname;
    }

    private sortDocuments(a: Document, b: Document, valueElement: string): number {
        switch (valueElement) {
            case 'date':
                return this.sortByString(a.modified, b.modified, 1);
            case 'name':
                return this.sortByString(a.modifiedBy, b.modifiedBy, 1);
            case 'type':
                return this.sortByString(a.SA_TransDocType, b.SA_TransDocType, 1);
            case 'document':
            default:
                return this.sortByString(a.description, b.description, 1);
        }
    }

    private sortByString(a, b, order: (1 | -1) = 1): 0 | 1 | -1 {
        if (a < b) {
            // @ts-ignore ignore return value error due to return being calculated
            return -1 * order;
        }
        if (a > b) {
            return order;
        }
        return 0;
    }
}
